import { RecordAlerts } from '../types';

/**
 * Synchronizes a list of alerts by updating the alerts for a specific record ID.
 *
 * If `nextAlerts` contains alerts for a record ID, this function will:
 * - Replace the existing alerts for that record ID in `prevAlerts` if found.
 * - Add a new entry if it doesn't already exist in `prevAlerts`.
 * - Remove the entry from `prevAlerts` if `nextAlerts` is empty.
 *
 * @param {RecordAlerts[]} prevAlerts - The existing list of alerts, possibly containing multiple record IDs.
 * @param {RecordAlerts[]} nextAlerts - The new alert set for a single record (only the first entry is used).
 * @param {string} [id] - The record ID that `nextAlerts` corresponds to.
 * @returns {RecordAlerts[]} A new list of alerts with the specified record’s alerts synchronized.
 */
export const syncAlertListByRecord = (
  prevAlerts: RecordAlerts[],
  nextAlerts: RecordAlerts[],
  id?: string,
): RecordAlerts[] => {
  // If no record ID is provided, return the original alerts list unchanged.
  if (!id) {
    return prevAlerts;
  }

  // Deep copy to avoid mutating the original array
  // TODO: Check performance (sometimes deep copying isnt needed and its just a performance hit)
  const parsedAlerts: RecordAlerts[] = JSON.parse(JSON.stringify(prevAlerts));

  // If there are no new alerts, that means the record id passed in is now triggering 0 alerts,
  // so remove the entry for this record ID
  if (!nextAlerts || nextAlerts.length === 0) {
    return parsedAlerts.filter(({ recordId }) => recordId !== id);
  }

  // Find the existing alert entry for this record ID
  const matchingEntry: RecordAlerts | undefined = parsedAlerts.find(({ recordId }) => recordId === id);

  if (!matchingEntry) {
    // If not found, add the new entry (assumes nextAlerts only contains one entry)
    parsedAlerts.push(nextAlerts[0]);
  } else {
    // Otherwise, replace the alerts array for the matched entry
    matchingEntry.alerts = nextAlerts[0].alerts;
  }

  return parsedAlerts;
};
